import * as stylex from "@stylexjs/stylex";
import { useState } from "react";
import type { ReactNode } from "react";
import { ExternalLink, Pause, Pencil, Play, X } from "lucide-react";
import type { BreakdownRow } from "@/lib/breakdown-sort";
import Badge, { type BadgeTone } from "@/components/ui/Badge";
import Button, { IconButton, pillProps } from "@/components/ui/Button";
import ConfirmDialog from "@/components/ui/ConfirmDialog";
import { PanelSection } from "@/components/ui/SidePanel";
import { Skeleton } from "@/components/ui/Skeleton";
import { SummaryBand } from "@/components/ui/Table";
import { useVideoBuffering } from "@/components/ui/useVideoBuffering";
import { recipes } from "@/design/recipes";
import "@/design/tokens.stylex";
import { colors, layout, radii, shadows, space, text } from "@/design/tokens.stylex";
import { statusActionFor, type CreativeStatusAction } from "@/lib/creative-status-action";
import { isRenderablePreviewUrl } from "@/lib/creative-preview";
import { clickThroughRate, costPerClick, postClickConversionRate, ratePerMille } from "@/lib/breakdown-sort";
import { formatRate } from "@/lib/breakdown-columns";
import { formatMetricValue } from "@/lib/grid-format";
import { platformAdUrl } from "@/lib/platform-ad-url";
import { entityStatusLabel, entityStatusTone, entityTypeLabel, platformLabel, type EntityStatusValue } from "@/lib/creative-entity";
export interface CreativeDetailStatus {
  delivery: EntityStatusValue | null;
  ad: EntityStatusValue | null;
  refreshedAt: string | null;
}
interface CreativeDetailProps {
  row: BreakdownRow;
  currency: string;
  // A friendlier account label when the Company knows one; the stable account
  // id is always shown as the fallback so identity is never lost.
  accountLabel?: string;
  // Live provider Delivery/Ad status for this entity (issue 11 contract),
  // fetched by the container. Absent while loading or for providers without a
  // live-status path (Google mutation/status remains deferred).
  status?: CreativeDetailStatus | null;
  statusLoading?: boolean;
  // Whether this provider exposes a live Delivery/Ad status read at all.
  statusSupported: boolean;
  // The full Edit workflow entry. Only rendered for members who may mutate — a
  // Viewer sees every read-only field but no mutation entry point.
  editHref: string;
  canEdit: boolean;
  // Commit a Pause or Resume for this ad. Absent for providers without an
  // equivalent live-Edit contract (Google) or when the caller cannot mutate.
  // The container re-reads the provider and reports back through toasts.
  onStatusAction?: (action: CreativeStatusAction) => void;
  statusActionPending?: boolean;
  onBack: () => void;
}

// The detail reads as the system's side panel (Mercury 11, 12): the facts sit
// in a white panel with a title and close at the top and the actions at its
// foot, beside a stage that shows the creative itself (Mercury 05). The page
// renders it in place of the list, so it stays in the flow rather than fixed.
const NARROW = "@media (max-width: 1023px)";
const styles = {
  stageBadge: {
    kVAEAm: "x10l6tqk",
    k87sOh: "x1gbt9f1",
    kbCHJM: "xgmsw5n",
    $$css: true
  },
  spinner: {
    kzqmXN: "x17z2i9w",
    kZKoxP: "x17rw0jw",
    kaIpWk: "x3kktt9",
    kMzoRj: "xdh2fpr",
    ksu8eU: "x1y0btm7",
    kVAM5u: "xzbo3x7",
    kLZC3w: "x11aw6a8",
    $$css: true
  },
  panel: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kmuXW: "x2lah0s",
    kzqmXN: "x1cy8gbr x14shcbm",
    k7Eaqz: "xeuugli",
    kGVxlE: "xi20zh2",
    kVQacm: "xb3r6kr",
    $$css: true
  },
  panelAlone: {
    kzqmXN: "xh8yej3",
    ks0D6T: "x11gisft",
    $$css: true
  },
  iconLink: {
    kzqmXN: "xjl3xoe",
    kg3NbH: "xnjsko4",
    $$css: true
  },
  band: {
    kLKAdn: "xav4c2n",
    $$css: true
  },
  factValue: {
    kogj98: "x1ghz6dp",
    kFhvOy: "x1qab1bc",
    ks0D6T: "x16em4ew",
    k9WMMc: "x1hr2gdg",
    kMwMTN: "xgwkh9f",
    k63SB2: "xk50ysn",
    kHjlTd: "xj0a0fe",
    $$css: true
  },
  absent: {
    kMwMTN: "xovanlf",
    k63SB2: "xo1l8bm",
    $$css: true
  }
};
function relativeTime(iso: string | null): string | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

// One Mapped conversion part: a measured value, a real measured zero, or an
// absent dash carrying the reason. Mirrors the table's measured-state contract
// so the detail and the row can never disagree.
function measuredPart(part: {
  value: number;
  state: BreakdownRow["conversionDetails"][number]["state"];
}, currency: string): {
  text: string;
  absent: boolean;
  title?: string;
} {
  switch (part.state) {
    case "measured":
    case "provisional":
      return {
        text: formatMetricValue(part.value, "number", currency),
        absent: false
      };
    case "zero":
      return {
        text: "0",
        absent: false
      };
    case "incomplete":
      return {
        text: "—",
        absent: true,
        title: "Incomplete"
      };
    case "unconfigured":
      return {
        text: "—",
        absent: true,
        title: "Not configured"
      };
    default:
      return {
        text: "—",
        absent: true,
        title: "Not received"
      };
  }
}
const STATUS_TONE: Record<ReturnType<typeof entityStatusTone>, BadgeTone> = {
  positive: "success",
  muted: "neutral",
  neutral: "info"
};
function StatusValue({
  label,
  supported,
  loading,
  status
}: {
  label: string;
  supported: boolean;
  loading: boolean;
  status: EntityStatusValue | null | undefined;
}) {
  if (!supported) return <Badge>Not available</Badge>;
  if (loading) {
    // The live status is on its way: a badge-sized skeleton holds the slot so
    // the value arriving does not shift the list.
    return <span role="status" aria-busy="true" aria-label={`${label} loading`}>
        <Skeleton width={64} height={20} />
      </span>;
  }
  return <span aria-label={`${label}: ${entityStatusLabel(status)}`}>
      <Badge tone={STATUS_TONE[entityStatusTone(status)]}>{entityStatusLabel(status)}</Badge>
    </span>;
}
function Fact({
  label,
  value,
  absent,
  title
}: {
  label: string;
  value: ReactNode;
  absent?: boolean;
  title?: string;
}) {
  return <>
      <dt className="x1i6qc9y">{label}</dt>
      <dd title={title} {...stylex.props(recipes.tabular, styles.factValue, absent && styles.absent)}>
        {value}
      </dd>
    </>;
}

// Provider CDNs may refuse a video whose request names our page; React's
// types do not know the attribute yet, so it is spread in.
const NO_REFERRER = {
  referrerPolicy: "no-referrer"
} as Record<string, string>;
export default function CreativeDetail({
  row,
  currency,
  accountLabel,
  status,
  statusLoading = false,
  statusSupported,
  editHref,
  canEdit,
  onStatusAction,
  statusActionPending = false,
  onBack
}: CreativeDetailProps) {
  const [confirmAction, setConfirmAction] = useState<CreativeStatusAction | null>(null);
  // Which action the ad's own fresh(ish) Ad status can offer. Only a clear
  // Active/Paused intent is toggleable; the confirmation re-reads the provider
  // before anything is written, so this is a display gate, not the guard.
  const statusAction = canEdit && statusSupported && onStatusAction && !statusLoading ? statusActionFor(status?.ad) : null;
  const providerUrl = row.adId ? platformAdUrl({
    platform: row.platform,
    accountId: row.accountId,
    campaignId: row.campaignId,
    adGroupId: row.adGroupId,
    adId: row.adId
  }) : null;
  const providerName = row.platform === "tiktok" ? "TikTok Ads Manager" : "Meta Ads Manager";
  // A broken, expired, or unsupported poster collapses to the neutral provider
  // fallback rather than a broken image (issue 14).
  const [posterFailed, setPosterFailed] = useState(false);
  // Playback starts on demand; while the provider's file buffers, a spinner
  // sits over the poster so a slow signed URL does not look like a dead player.
  const video = useVideoBuffering(false);
  const posterUrl = !posterFailed && isRenderablePreviewUrl(row.thumbnailUrl) ? row.thumbnailUrl : null;
  const isVideo = row.format === "Video";
  // Detail may load playable media on demand; the table never does. preload="none"
  // keeps even the detail from fetching video bytes until the user presses play.
  const playableMedia = isVideo && isRenderablePreviewUrl(row.previewMediaUrl) ? row.previewMediaUrl : null;
  // A search ad has nothing to show; the facts then take the space alone.
  const hasPreview = isRenderablePreviewUrl(row.thumbnailUrl) || Boolean(playableMedia);
  const cpm = ratePerMille(row);
  const ctr = clickThroughRate(row);
  const cpc = costPerClick(row);
  const cvr = postClickConversionRate(row);
  const refreshed = relativeTime(status?.refreshedAt ?? null);
  return <section aria-label="Creative detail" className="x78zum5 x1q0g3np x1dh0ydc x1cy8zhl x1rw78eo x1dfszw7 xeuugli">
      {hasPreview ? <div className="x1n2onr6 x1iyjqo2 x1r8uery xeuugli x78zum5 x6s0dn4 xl56j7k x1n46bim x1xkdi7j xze2s60 xhrzdqn x19qxo9d xb3r6kr">
          {playableMedia ? <>
              <video controls preload="none" playsInline poster={posterUrl ?? undefined} src={playableMedia} {...NO_REFERRER} aria-busy={video.buffering} {...video.handlers} className="x1lliihq x193iq5w xmz0i5r x16l1o21 x19kjcj4 xh8yej3 x5yr21d x2jzp15" />
              {video.buffering ? <span role="status" aria-label="Loading video" className="x10l6tqk x10a8y8t xrvj5dj x1ku5rj1 x47corl">
                  <span {...stylex.props(styles.spinner, recipes.spinning)} />
                </span> : null}
            </> : posterUrl ? <img src={posterUrl} alt="" referrerPolicy="no-referrer" onError={() => setPosterFailed(true)} className="x1lliihq x193iq5w xmz0i5r x16l1o21 x19kjcj4" /> : <span data-preview="fallback" className="xrvj5dj x1ku5rj1 x1oysuqx x1m3v4wt x16l1o21 xlcwlql x1i6qc9y x7x6yu3 x1s688f x1dgsrnt xtvhhri">
              {platformLabel(row.platform)}
            </span>}
          {isVideo && !playableMedia ? <Badge xstyle={styles.stageBadge}>Video</Badge> : null}
        </div> : null}

      <div {...stylex.props(recipes.surface, styles.panel, !hasPreview && styles.panelAlone)}>
        <header className="x78zum5 x1cy8zhl x166cprz xh0uz7e xf8hsz2 x1qn9uzo">
          <div className="x1iyjqo2 xeuugli x67pdoy">
            <div className="x78zum5 x6s0dn4 x1a02dak x166cprz xag81yp x7x6yu3 x1i6qc9y">
              <span>
                {platformLabel(row.platform)} · {row.format}
              </span>
              {row.entityType === "asset" ? <Badge tone={row.additive ? "neutral" : "warning"} title={row.additive ? undefined : "Performance Max asset — several assets can share one delivery, so these rows are not additive."}>
                  {entityTypeLabel(row.entityType)}
                </Badge> : null}
            </div>
            <h2 className="x1ghz6dp xxmgfec xk50ysn xgwkh9f xj0a0fe">{row.name}</h2>
          </div>
          <div className="x78zum5 x6s0dn4 xsuxsxl">
            {providerUrl ? <a href={providerUrl} target="_blank" rel="noreferrer" aria-label={`Open in ${providerName}`} title={`Open in ${providerName}`} {...pillProps({
            variant: "ghost",
            xstyle: styles.iconLink
          })}>
                <ExternalLink aria-hidden size={15} />
              </a> : null}
            <IconButton aria-label="Back to creatives" onClick={onBack}>
              <X size={16} />
            </IconButton>
          </div>
        </header>

        <div className="xf8hsz2 x1f9svqt">
          <SummaryBand xstyle={styles.band} figures={[{
          label: "Spend",
          value: formatMetricValue(row.spend, "currency", currency)
        }, {
          label: "Conversions",
          value: formatMetricValue(row.conversions, "number", currency)
        }, {
          label: "Cost / conversion",
          value: row.costPerConversion == null ? "—" : formatMetricValue(row.costPerConversion, "currency_per", currency)
        }]} />

          <PanelSection title="Status">
            {!statusSupported ? <p className="x1ghz6dp x7x6yu3 x1i6qc9y">
                Live status is not available for {platformLabel(row.platform)} yet.
              </p> : <>
                <dl className="xrvj5dj xju1xpo x6s0dn4 xjpaz5y xba9wbz x1ghz6dp xtu3avg">
                  <Fact label="Delivery status" value={<StatusValue label="Delivery status" supported={statusSupported} loading={statusLoading} status={status?.delivery} />} />
                  <Fact label="Ad status" value={<StatusValue label="Ad status" supported={statusSupported} loading={statusLoading} status={status?.ad} />} />
                </dl>
                <p className="x1ghz6dp x7x6yu3 x1i6qc9y">
                  {statusLoading ? "Checking current provider status…" : refreshed ? `Status refreshed ${refreshed}.` : "Provider status has not been refreshed yet."}
                </p>
              </>}
          </PanelSection>

          <PanelSection title="Metrics">
            <dl className="xrvj5dj xju1xpo x6s0dn4 xjpaz5y xba9wbz x1ghz6dp xtu3avg">
              <Fact label="Impressions" value={formatMetricValue(row.impressions, "number", currency)} />
              <Fact label="CPM" value={cpm == null ? "—" : formatMetricValue(cpm, "currency", currency)} absent={cpm == null} />
              <Fact label="Clicks" value={formatMetricValue(row.clicks, "number", currency)} />
              <Fact label="CTR" value={formatRate(ctr)} absent={ctr == null} />
              <Fact label="CPC" value={cpc == null ? "—" : formatMetricValue(cpc, "currency", currency)} absent={cpc == null} />
              <Fact label="Conversion rate" value={formatRate(cvr)} absent={cvr == null} />
            </dl>
          </PanelSection>

          {row.conversionDetails.length > 0 ? <PanelSection title="Conversions">
              <dl className="xrvj5dj xju1xpo x6s0dn4 xjpaz5y xba9wbz x1ghz6dp xtu3avg">
                {row.conversionDetails.map(part => {
              const cell = measuredPart(part, currency);
              return <Fact key={part.name} label={part.name} value={cell.text} absent={cell.absent} title={cell.title} />;
            })}
              </dl>
            </PanelSection> : null}

          <PanelSection title="Identity">
            <dl className="xrvj5dj xju1xpo x6s0dn4 xjpaz5y xba9wbz x1ghz6dp xtu3avg">
              <Fact label="Account" value={accountLabel ?? row.accountId} title={row.accountId} />
              <Fact label="Campaign" value={row.campaignId ?? "—"} absent={!row.campaignId} />
              <Fact label="Ad group" value={row.adGroupId ?? "—"} absent={!row.adGroupId} />
            </dl>
          </PanelSection>
        </div>

        <footer className="x78zum5 x6s0dn4 x1a02dak x166cprz x16orzk0 xf8hsz2 x178xt8z x13fuv20 x13hb0vi">
          {canEdit ? <>
              {statusAction ? <Button size="sm" disabled={statusActionPending} onClick={() => setConfirmAction(statusAction)} icon={statusAction === "pause" ? <Pause size={13} /> : <Play size={13} />}>
                  {statusActionPending ? "Applying…" : statusAction === "pause" ? "Pause ad" : "Resume ad"}
                </Button> : null}
              <a href={editHref} {...pillProps({
            size: "sm",
            withIcon: true
          })}>
                <Pencil aria-hidden size={13} /> Edit in Launch
              </a>
            </> : <p className="x1ghz6dp x7x6yu3 x1i6qc9y">
              You have read-only access, so editing this ad is not available.
            </p>}
        </footer>
      </div>

      <ConfirmDialog open={confirmAction !== null} title={confirmAction === "pause" ? "Pause this ad?" : "Resume this ad?"} message={confirmAction === "pause" ? "Pausing stops this ad from delivering. Its live status is re-checked first, and the change is stopped if the ad or its campaign moved since you opened this." : "Resuming lets this ad deliver again, subject to its campaign and ad group. Its live status is re-checked first, and the change is stopped if it moved since you opened this."} confirmLabel={confirmAction === "pause" ? "Pause ad" : "Resume ad"} onCancel={() => setConfirmAction(null)} onConfirm={() => {
      const action = confirmAction;
      setConfirmAction(null);
      if (action) onStatusAction?.(action);
    }} />
    </section>;
}